(() => {
  function initializeGoogleMapsScript(apiKey) {
    if (!apiKey || window.googleMapsApi) {
      return;
    }

    window.googleMapsApi = true;
    (function (d, script) {
      script = d.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.onload = function () {
        registerMapLocationsCustomElement();
      };
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
      d.getElementsByTagName("head")[0].appendChild(script);
    })(document);
  }

  function registerMapLocationsCustomElement() {
  if (!customElements.get("map-locations")) {
    customElements.define(
      "map-locations",
      class MapLocations extends HTMLElement {
        constructor() {
          super();
        }

        connectedCallback() {
          this.init();
        }

        init = async () => {
          this.zoom = parseInt(this.getAttribute("zoom"), 10) || 14;
          this.addressElements = this.querySelectorAll("[data-address]");
          this.resetButtonElement = this.querySelector(".map-card-button-reset");
          this.markerImageUrl = this.getAttribute("marker-image-url");
          this.addresses = [...this.addressElements].map((address) => {
            return address.dataset.address;
          });
          this.markers = [];
          this.infoWindows = [];
          this.geocoder = new google.maps.Geocoder();
          const colors = this.getAttribute("colors");
          this.map = new google.maps.Map(
            document.getElementById(`Map-${this.getAttribute("section-id")}`),
            {
              zoom: this.zoom,
              styles: colors ? getMapStyles(JSON.parse(colors)) : undefined,
            },
          );
          await this.populateMap();

          this.handleLocationClick = this.handleLocationClick.bind(this);
          this.querySelectorAll(".js-location-trigger").forEach((location) => {
            location.addEventListener("click", this.handleLocationClick);
          });

          this.handleReset = this.handleReset.bind(this);
          if (this.resetButtonElement) {
            this.resetButtonElement.addEventListener("click", this.handleReset);
          }
        };

        populateMap = async () => {
          // Markers / Geocoding. Card index is carried through so a failed
          // geocode can't shift markers, icons, or info windows off by one.
          const geocoded = [];
          for (const [index, address] of this.addresses.entries()) {
            const cached = MapLocations.geocodeCache.get(address);
            if (cached) {
              geocoded.push({ index, position: cached });
              continue;
            }
            try {
              const response = await MapLocations.codeAddress({
                address,
                geocoder: this.geocoder,
              });
              const location = response.results[0].geometry.location;
              const position = { lat: location.lat(), lng: location.lng() };
              MapLocations.geocodeCache.set(address, position);
              geocoded.push({ index, position });
            } catch (error) {
              console.error(error);
            }
            // This artificial delay is necessary as the Geocoder has a rate limiter.
            await sleep(120);
          }

          this.markersByCardIndex = {};
          geocoded.forEach(({ index, position }, i) => {
            if (i === 0) {
              this.map.setCenter(position);
            }

            const marker = new google.maps.Marker({
              map: this.map,
              position,
              icon: this.getMarkerIcon(index),
            });

            const infoWindow = new google.maps.InfoWindow({
              content: this.getInfoWindow(index),
            });

            marker.addListener("click", () => {
              if (this.activeInfoWindow) {
                this.activeInfoWindow.close();
              }

              infoWindow.open({
                anchor: marker,
                map: this.map,
                shouldFocus: false,
              });

              this.activeInfoWindow = infoWindow;
            });

            this.markers.push(marker);
            this.infoWindows.push(infoWindow);
            this.markersByCardIndex[index] = marker;
          });

          if (this.markers.length > 1) {
            this.bounds = new google.maps.LatLngBounds();
            this.markers.forEach((marker) => {
              this.bounds.extend(marker.getPosition());
            });
            this.map.setCenter(this.bounds.getCenter());
            this.map.fitBounds(this.bounds);
          }
        };

        handleLocationClick(event) {
          event.preventDefault();
          const element = event.currentTarget.closest(".section-map-locations-location");
          const index = [...element.parentElement.children].indexOf(element);
          const marker = this.markersByCardIndex?.[index];
          if (!marker) {
            return;
          }
          this.map.setCenter(marker.getPosition());
          this.map.setZoom(this.zoom);
          this.resetButtonElement?.classList.remove("visually-hidden");
        }

        getMarkerIcon(cardIndex) {
          const card = this.addressElements[cardIndex]?.closest(
            ".section-map-locations-location",
          );
          const icon = MapLocations.pinIcons[card?.dataset.pin];
          if (icon) {
            return icon();
          }
          return this.markerImageUrl ?? undefined;
        }

        static svgIcon = (svg, width, height, anchorX, anchorY) => ({
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
          scaledSize: new google.maps.Size(width, height),
          anchor: new google.maps.Point(anchorX, anchorY),
        });

        static pinIcons = {
          star: () =>
            MapLocations.svgIcon(
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36"><path d="M18 2l4.9 9.9L34 13.5l-8 7.8 1.9 11L18 27.2 8.1 32.3 10 21.3l-8-7.8 11.1-1.6z" fill="#cc0000" stroke="#7a0000" stroke-width="1.5" stroke-linejoin="round"/></svg>',
              34,
              34,
              17,
              17,
            ),
          "coming-soon": () =>
            MapLocations.svgIcon(
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36"><path d="M12 1C5.9 1 1 5.9 1 12c0 8.2 11 23 11 23s11-14.8 11-23C23 5.9 18.1 1 12 1z" fill="#ffd43b" stroke="#8a6d00" stroke-width="1.5"/><circle cx="12" cy="12" r="4.5" fill="#8a6d00"/></svg>',
              26,
              39,
              13,
              39,
            ),
        };

        // Geocode results barely change — cache them locally so repeat visits
        // skip the rate-limited Geocoder entirely.
        static geocodeCache = {
          KEY: "acg-wtb-geocode-v1",
          TTL: 1000 * 60 * 60 * 24 * 30,
          read() {
            try {
              return JSON.parse(localStorage.getItem(this.KEY)) || {};
            } catch {
              return {};
            }
          },
          get(address) {
            const entry = this.read()[address];
            if (!entry || Date.now() - entry.t > this.TTL) {
              return null;
            }
            return { lat: entry.lat, lng: entry.lng };
          },
          set(address, position) {
            try {
              const all = this.read();
              all[address] = { ...position, t: Date.now() };
              localStorage.setItem(this.KEY, JSON.stringify(all));
            } catch {
              // Storage full or blocked — caching is best-effort.
            }
          },
        };

        handleReset() {
          if (this.bounds) {
            this.map.setCenter(this.bounds.getCenter());
            this.map.fitBounds(this.bounds);
            this.resetButtonElement?.classList.add("visually-hidden");
          }
        }

        static codeAddress = async ({ address, geocoder }) => {
          if (!address || !geocoder) {
            return null;
          }

          return geocoder.geocode({ address });
        };

        getInfoWindow(markerIndex) {
          const addressElement = this.addressElements[markerIndex].closest(
            ".section-map-locations-location",
          );

          if (addressElement.dataset.info) {
            return `<div class="map-info-window">${addressElement.dataset.info}</div>`;
          }

          const title = addressElement.querySelector(".location-title")?.textContent || "";
          const content = addressElement.querySelector(".location-address")?.innerHTML;

          return `<div class="map-info-window"><h4>${title}</h4>${content}</div>`;
        }
      },
    );
  }
}

  function getMapStyles(colors) {
  if (!colors) {
    return [];
  }

  return [
    {
      elementType: "geometry",
      stylers: [
        {
          color: colors.background,
        },
      ],
    },
    {
      elementType: "labels.icon",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      elementType: "labels.text.fill",
      stylers: [
        {
          color: colors.foreground,
        },
      ],
    },
    {
      elementType: "labels.text.stroke",
      stylers: [
        {
          color: colors.background,
        },
      ],
    },
    {
      featureType: "administrative",
      elementType: "geometry",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "administrative.country",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "administrative.land_parcel",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "administrative.neighborhood",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "administrative.locality",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "poi",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "road",
      elementType: "geometry.fill",
      stylers: [
        {
          color: colors.fb_3,
        },
      ],
    },
    {
      featureType: "road",
      elementType: "labels.icon",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "road.arterial",
      elementType: "geometry",
      stylers: [
        {
          color: colors.fb_2,
        },
      ],
    },
    {
      featureType: "road.highway",
      elementType: "geometry",
      stylers: [
        {
          color: colors.fb_1,
        },
      ],
    },
    {
      featureType: "road.highway.controlled_access",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "road.local",
      elementType: "labels.text.fill",
      stylers: [
        {
          color: colors.fb_1,
        },
      ],
    },
    {
      featureType: "road.local",
      elementType: "labels.text.stroke",
      stylers: [
        {
          color: colors.background,
        },
      ],
    },
    {
      featureType: "transit",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [
        {
          color: colors.bm,
        },
      ],
    },
  ];
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Expose only the Maps API callback globally — everything else stays scoped.
  window.initializeGoogleMapsScript = initializeGoogleMapsScript;

  // Autorun: load the Maps script if a section on the page provides an API key.
  const sections = document.querySelectorAll("map-locations");
  const sectionWithApiKey = [...sections].find((section) =>
    section.getAttribute("google-maps-api-key"),
  );
  const apiKey = sectionWithApiKey?.getAttribute("google-maps-api-key");

  // No section found for some reason or maps API is already loaded/loading.
  if ((!sectionWithApiKey && !Shopify.designMode) || window.googleMapsApi) {
    return;
  }

  if (apiKey) {
    initializeGoogleMapsScript(apiKey);
    return;
  }
})();
